import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/services/api";
import { Loader2, PackageCheck, RotateCcw } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;

const ESTADO_STYLE = {
  solicitado: "bg-gray-100 text-gray-700",
  aprobado: "bg-blue-100 text-blue-800",
  preparado: "bg-indigo-100 text-indigo-800",
  despachado: "bg-amber-100 text-amber-800",
  recibido: "bg-green-100 text-green-800",
  parcial: "bg-orange-100 text-orange-800",
  rechazado: "bg-red-100 text-red-700",
};

export default function MayoristaPedidos() {
  const [pedidos, setPedidos] = useState(null);
  const [recepcion, setRecepcion] = useState(null); // pedido en recepción
  const [cantidades, setCantidades] = useState({});
  const [observaciones, setObservaciones] = useState("");
  const [guardando, setGuardando] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/mayoristas/me/pedidos");
      setPedidos(res.data);
    } catch (e) {
      toast.error("Error cargando pedidos");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const abrirRecepcion = (pedido) => {
    setRecepcion(pedido);
    const init = {};
    pedido.items.forEach((i) => (init[i.producto_id] = i.cantidad));
    setCantidades(init);
    setObservaciones("");
  };

  const confirmarRecepcion = async () => {
    setGuardando(true);
    try {
      const res = await api.post(`/mayoristas/me/pedidos/${recepcion.id}/confirmar-recepcion`, {
        cantidades,
        observaciones,
      });
      toast.success(
        res.data.diferencias?.length
          ? "Recepción registrada con diferencias — se alertó a la central"
          : "Recepción confirmada"
      );
      setRecepcion(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al confirmar recepción");
    } finally {
      setGuardando(false);
    }
  };

  const repetir = async (pedido) => {
    try {
      const res = await api.post(`/mayoristas/me/pedidos/${pedido.id}/repetir`);
      toast.success(`Pedido ${res.data.numero} creado`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al repetir");
    }
  };

  if (!pedidos) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mayorista-pedidos-page">
      <h2 className="text-lg font-bold text-[#4A2C0A]">Mis Pedidos a Bodega</h2>

      {pedidos.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-10">Aún no tienes pedidos. ¡Haz tu primera compra!</p>
      )}

      {pedidos.map((p) => (
        <div key={p.id} className="bg-white rounded-2xl border p-4" data-testid={`pedido-card-${p.numero}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-[#4A2C0A]">{p.numero}</p>
            <Badge className={`${ESTADO_STYLE[p.estado] || "bg-gray-100"} border-0 capitalize`}>
              {p.estado}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            {new Date(p.fecha_creacion).toLocaleString("es-CO")} · {p.unidades} unidades
            {p.factura_numero && ` · Factura ${p.factura_numero}`}
          </p>
          <div className="space-y-1 mb-2">
            {p.items.map((i) => (
              <div key={i.producto_id} className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {i.cantidad}× {i.nombre}
                </span>
                <span>{fmt(i.subtotal)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-bold text-[#4A2C0A] border-t pt-2">
            <span>Total</span>
            <span>{fmt(p.total)}</span>
          </div>
          <p className="text-xs text-green-700 mt-1">Margen estimado: {fmt(p.margen_estimado)}</p>

          {p.diferencias?.length > 0 && (
            <div className="mt-2 bg-orange-50 rounded-lg p-2 text-xs text-orange-700">
              Diferencias reportadas:{" "}
              {p.diferencias.map((d) => `${d.producto} (enviada ${d.enviada}, recibida ${d.recibida})`).join(", ")}
            </div>
          )}

          <div className="flex gap-2 mt-3">
            {p.estado === "despachado" && (
              <Button
                size="sm"
                className="bg-green-700 hover:bg-green-800 flex-1"
                onClick={() => abrirRecepcion(p)}
                data-testid={`confirmar-recepcion-btn-${p.numero}`}
              >
                <PackageCheck className="w-4 h-4 mr-1" /> Confirmar Recepción
              </Button>
            )}
            {["recibido", "parcial"].includes(p.estado) && (
              <Button size="sm" variant="outline" onClick={() => repetir(p)} data-testid={`repetir-btn-${p.numero}`}>
                <RotateCcw className="w-4 h-4 mr-1" /> Repetir
              </Button>
            )}
          </div>
        </div>
      ))}

      <Dialog open={!!recepcion} onOpenChange={(o) => !o && setRecepcion(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Recepción — {recepcion?.numero}</DialogTitle>
            <DialogDescription>Verifica las cantidades recibidas</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {recepcion?.items.map((i) => (
              <div key={i.producto_id} className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium">{i.nombre}</p>
                  <p className="text-xs text-gray-500">Enviadas: {i.cantidad}</p>
                </div>
                <input
                  type="number"
                  min="0"
                  value={cantidades[i.producto_id] ?? i.cantidad}
                  onChange={(e) =>
                    setCantidades({ ...cantidades, [i.producto_id]: parseInt(e.target.value) || 0 })
                  }
                  className="w-20 text-center border rounded-lg h-9 font-semibold"
                  data-testid={`recepcion-qty-${i.nombre}`}
                />
              </div>
            ))}
            <Textarea
              placeholder="Observaciones (opcional)"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              data-testid="recepcion-observaciones"
            />
            <Button
              className="w-full bg-green-700 hover:bg-green-800"
              onClick={confirmarRecepcion}
              disabled={guardando}
              data-testid="confirmar-recepcion-submit"
            >
              {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar Recepción
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
